<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import EmptyState from '../../../../components/widgets/EmptyState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import WahaSessionAPI from 'dashboard/api/wahaSession';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const qrCodes = reactive({
  whatsapp: '',
  messenger: '',
  telegram: '',
});

const wahaSession = ref(null);
const wahaQrCodeUrl = ref('');
const showPhoneOption = ref(false);
const phoneNumber = ref('');
const pairingCode = ref('');
const requestingCode = ref(false);
const qrRefreshInterval = ref(null);
const isAuthenticated = ref(false);

const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](route.params.inbox_id)
);

const accountId = computed(() => route.params.accountId);

const isWahaV2Channel = computed(() => {
  if (!currentInbox.value) {
    console.log('isWahaV2Channel: no currentInbox');
    return false;
  }

  // The inbox serializer puts channel attributes directly on the inbox object
  // not in a nested channel object
  const isApi = currentInbox.value.channel_type === 'Channel::Api';
  const hasWahaFlag =
    currentInbox.value.additional_attributes?.waha_v2 === true;

  console.log('isWahaV2Channel check:', {
    inboxId: currentInbox.value.id,
    channelType: currentInbox.value.channel_type,
    additionalAttributes: currentInbox.value.additional_attributes,
    isApi,
    hasWahaFlag,
    result: isApi && hasWahaFlag,
  });

  return isApi && hasWahaFlag;
});

// Use useInbox composable with the inbox ID
const {
  isAWhatsAppCloudChannel,
  isATwilioChannel,
  isASmsInbox,
  isALineChannel,
  isAnEmailChannel,
  isAWhatsAppChannel,
  isAFacebookInbox,
  isATelegramChannel,
  isATwilioWhatsAppChannel,
  isAnAfricasTalkingChannel,
} = useInbox(route.params.inbox_id);

const hasDuplicateInstagramInbox = computed(() => {
  const instagramId = currentInbox.value.instagram_id;
  const facebookInbox =
    store.getters['inboxes/getFacebookInboxByInstagramId'](instagramId);

  return (
    currentInbox.value.channel_type === INBOX_TYPES.INSTAGRAM && facebookInbox
  );
});

const shouldShowWhatsAppWebhookDetails = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source !== 'embedded_signup'
  );
});

const isWhatsAppEmbeddedSignup = computed(() => {
  return (
    isAWhatsAppCloudChannel.value &&
    currentInbox.value.provider_config?.source === 'embedded_signup'
  );
});

const message = computed(() => {
  if (isWahaV2Channel.value) {
    return t('INBOX_MGMT.WAHA_V2.SCAN_QR_CODE');
  }

  if (isATwilioChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAnAfricasTalkingChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.SMS.AFRICAS_TALKING.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isASmsInbox.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.SMS.BANDWIDTH.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isALineChannel.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAWhatsAppCloudChannel.value && shouldShowWhatsAppWebhookDetails.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.SUBTITLE'
    )}`;
  }

  if (isAnEmailChannel.value && !currentInbox.value.provider) {
    return t('INBOX_MGMT.ADD.EMAIL_CHANNEL.FINISH_MESSAGE');
  }

  if (currentInbox.value.web_widget_script) {
    return t('INBOX_MGMT.FINISH.WEBSITE_SUCCESS');
  }

  if (isWhatsAppEmbeddedSignup.value) {
    return `${t('INBOX_MGMT.FINISH.MESSAGE')}. ${t(
      'INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION'
    )}`;
  }

  return t('INBOX_MGMT.FINISH.MESSAGE');
});

async function generateQRCode(platform, identifier) {
  if (!identifier || !identifier.trim()) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid identifier for ${platform} QR code`);
    return;
  }

  try {
    const platformUrls = {
      whatsapp: id => `https://wa.me/${id}`,
      messenger: id => `https://m.me/${id}`,
      telegram: id => `https://t.me/${id}`,
    };

    const url = platformUrls[platform](identifier);
    const qrDataUrl = await QRCode.toDataURL(url);
    qrCodes[platform] = qrDataUrl;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error generating ${platform} QR code:`, error);
    qrCodes[platform] = '';
  }
}

async function loadWahaSession() {
  if (!isWahaV2Channel.value || !accountId.value) return;

  try {
    const response = await WahaSessionAPI.getSessions(accountId.value);
    const sessions = response.data.sessions || [];
    wahaSession.value = sessions.find(
      s => s.inbox_id === parseInt(route.params.inbox_id)
    );

    if (wahaSession.value) {
      await fetchQRCode();
      startQRCodeRefresh();
    }
  } catch (error) {
    console.error('Failed to load WAHA session:', error);
  }
}

async function fetchQRCode() {
  if (!wahaSession.value || !accountId.value || isAuthenticated.value) return;

  try {
    const response = await WahaSessionAPI.getQRCode(
      accountId.value,
      wahaSession.value.id
    );
    const blob = response.data;
    wahaQrCodeUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    console.error('Failed to fetch QR code:', error);
    // If session is already authenticated, stop polling
    if (
      error.response?.data?.error?.includes('authenticated') ||
      error.response?.data?.error?.includes('WORKING')
    ) {
      isAuthenticated.value = true;
      stopQRCodeRefresh();
    }
  }
}

function startQRCodeRefresh() {
  if (!wahaSession.value || isAuthenticated.value) return;

  // Clear any existing interval
  if (qrRefreshInterval.value) {
    clearInterval(qrRefreshInterval.value);
  }

  qrRefreshInterval.value = setInterval(async () => {
    if (!showPhoneOption.value && wahaSession.value && !isAuthenticated.value) {
      await fetchQRCode();
    }
  }, 5000);
}

function stopQRCodeRefresh() {
  if (qrRefreshInterval.value) {
    clearInterval(qrRefreshInterval.value);
    qrRefreshInterval.value = null;
  }
}

function togglePhoneOption() {
  showPhoneOption.value = !showPhoneOption.value;
}

async function requestPairingCode() {
  if (!phoneNumber.value || !wahaSession.value) return;

  requestingCode.value = true;
  try {
    const response = await WahaSessionAPI.requestPairingCode(
      accountId.value,
      wahaSession.value.id,
      phoneNumber.value
    );
    pairingCode.value = response.data.code;
  } catch (error) {
    console.error('Failed to request pairing code:', error);
    alert('Failed to request pairing code. Please try again.');
  } finally {
    requestingCode.value = false;
  }
}

async function generateQRCodes() {
  if (!currentInbox.value) return;

  // WAHA v2 channels
  if (isWahaV2Channel.value) {
    await loadWahaSession();
    return;
  }

  // WhatsApp (both Cloud and Twilio)
  if (currentInbox.value.phone_number && isAWhatsAppChannel.value) {
    // For Twilio WhatsApp, phone_number format is "whatsapp:+1234567890"
    // Extract just the phone number part for QR code generation
    const phoneNumber = currentInbox.value.phone_number.replace(
      'whatsapp:',
      ''
    );
    await generateQRCode('whatsapp', phoneNumber);
  }

  // Facebook Messenger
  if (currentInbox.value.page_id && isAFacebookInbox.value) {
    await generateQRCode('messenger', currentInbox.value.page_id);
  }

  // Telegram
  if (isATelegramChannel.value && currentInbox.value.bot_name) {
    await generateQRCode('telegram', currentInbox.value.bot_name);
  }
}

// Watch for currentInbox changes and regenerate QR codes when available
watch(
  currentInbox,
  newInbox => {
    if (newInbox) {
      generateQRCodes();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  // Ensure inbox is loaded from store
  await store.dispatch('inboxes/get');
  generateQRCodes();
});
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <DuplicateInboxBanner
      v-if="hasDuplicateInstagramInbox"
      :content="$t('INBOX_MGMT.ADD.INSTAGRAM.NEW_INBOX_SUGGESTION')"
    />
    <EmptyState
      :title="$t('INBOX_MGMT.FINISH.TITLE')"
      :message="message"
      :button-text="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
    >
      <div class="w-full text-center">
        <div class="my-4 mx-auto max-w-[70%]">
          <woot-code
            v-if="currentInbox.web_widget_script"
            :script="currentInbox.web_widget_script"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isATwilioWhatsAppChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isAnAfricasTalkingChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div
          v-if="shouldShowWhatsAppWebhookDetails"
          class="w-[50%] max-w-[50%] ml-[25%]"
        >
          <p class="mt-8 font-medium text-slate-700 dark:text-slate-200">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_URL') }}
          </p>
          <woot-code lang="html" :script="currentInbox.callback_webhook_url" />
          <p class="mt-8 font-medium text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.API_CALLBACK.WEBHOOK_VERIFICATION_TOKEN'
              )
            }}
          </p>
          <woot-code
            lang="html"
            :script="currentInbox.provider_config.webhook_verify_token"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isALineChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div class="w-[50%] max-w-[50%] ml-[25%]">
          <woot-code
            v-if="isASmsInbox && !isAnAfricasTalkingChannel"
            lang="html"
            :script="currentInbox.callback_webhook_url"
          />
        </div>
        <div
          v-if="isAnEmailChannel && !currentInbox.provider"
          class="w-[50%] max-w-[50%] ml-[25%]"
        >
          <woot-code lang="html" :script="currentInbox.forward_to_email" />
        </div>
        <!-- WAHA v2 QR Code / Phone Code -->
        <div
          v-if="isWahaV2Channel"
          class="flex flex-col gap-6 mx-auto mt-8 max-w-md"
        >
          <!-- QR Code Section -->
          <div v-if="!showPhoneOption" class="flex flex-col items-center">
            <p class="mb-6 text-sm text-center text-slate-11">
              {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.SCAN_QR_DESC') }}
            </p>

            <div class="p-4 bg-white rounded-lg border border-slate-6">
              <img
                :src="wahaQrCodeUrl"
                alt="WhatsApp QR Code"
                class="w-64 h-64"
                @error="() => (wahaQrCodeUrl = wahaQrCodeUrl + '&retry=1')"
              />
            </div>

            <button
              class="mt-6 text-sm text-woot-500 hover:text-woot-600"
              @click="togglePhoneOption"
            >
              {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.USE_PHONE_CODE') }}
            </button>
          </div>

          <!-- Phone Code Section -->
          <div v-else class="flex flex-col max-w-md mx-auto">
            <p class="mb-6 text-sm text-slate-11">
              {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.PHONE_CODE_DESC') }}
            </p>

            <div v-if="!pairingCode" class="flex flex-col gap-4">
              <input
                v-model="phoneNumber"
                type="tel"
                placeholder="+1234567890"
                class="px-4 py-2 rounded-md border border-slate-6"
              />
              <button
                :disabled="!phoneNumber || requestingCode"
                class="px-4 py-2 text-white bg-woot-500 rounded-md hover:bg-woot-600 disabled:opacity-50"
                @click="requestPairingCode"
              >
                {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.REQUEST_CODE') }}
              </button>
            </div>

            <div
              v-else
              class="p-6 bg-yellow-50 rounded-lg border border-yellow-200"
            >
              <p class="mb-2 text-sm font-medium text-yellow-900">
                {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.PAIRING_CODE') }}
              </p>
              <div class="flex justify-center py-3">
                <code
                  class="text-3xl font-bold tracking-widest text-yellow-900"
                >
                  {{ pairingCode }}
                </code>
              </div>
              <p class="mt-2 text-xs text-center text-yellow-900">
                {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.ENTER_CODE_IN_WHATSAPP') }}
              </p>
            </div>

            <button
              class="mt-6 text-sm text-woot-500 hover:text-woot-600"
              @click="togglePhoneOption"
            >
              {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.USE_QR_CODE') }}
            </button>
          </div>
        </div>

        <div
          v-if="isAWhatsAppChannel && qrCodes.whatsapp"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.WHATSAPP_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.whatsapp"
              alt="WhatsApp QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isAFacebookInbox && qrCodes.messenger"
          class="flex flex-col gap-3 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.MESSENGER_QR_INSTRUCTION') }}
          </p>
          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.messenger"
              alt="Messenger QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div
          v-if="isATelegramChannel && qrCodes.telegram"
          class="flex flex-col gap-4 items-center mt-8"
        >
          <p class="mt-2 text-sm text-n-slate-9">
            {{ $t('INBOX_MGMT.FINISH.TELEGRAM_QR_INSTRUCTION') }}
          </p>

          <div class="rounded-lg shadow outline-1 outline-n-strong outline">
            <img
              :src="qrCodes.telegram"
              alt="Telegram QR Code"
              class="rounded-lg size-48 dark:invert"
            />
          </div>
        </div>
        <div class="flex gap-2 justify-center mt-4">
          <router-link
            :to="{
              name: 'settings_inbox_show',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              outline
              slate
              :label="$t('INBOX_MGMT.FINISH.MORE_SETTINGS')"
            />
          </router-link>
          <router-link
            :to="{
              name: 'inbox_dashboard',
              params: { inboxId: $route.params.inbox_id },
            }"
          >
            <NextButton
              solid
              teal
              :label="$t('INBOX_MGMT.FINISH.BUTTON_TEXT')"
            />
          </router-link>
        </div>
      </div>
    </EmptyState>
  </div>
</template>
